import _ from 'lodash'
import ChartDesignerPanel from './chart-designer-panel'
import activeTemplate from './other-settings-active-content.hbs'
import SettingView from './setting-view'
import ColorSettings from './color-settings'
import TotalContainerValueSetting from './total-container-value-setting'
import appDisplay from 'scoreboard/components/app-display'
import LabelSettings from './label-settings'
import LegendSettings from './legend-settings'
import EditAxesMenu from './edit-axes-menu'
import StartingViewMenu from './starting-view-menu'
import Tooltip from 'scoreboard/components/tooltip'
import nonAxesPlotType from 'scoreboard/charts/non-axes-plot-type'
import { chart as isPolarChart } from 'scoreboard/charts/is-polar'
import BootslideMenu from 'scoreboard/components/bootslide/bootslide-menu'
import i18n from 'scoreboard/components/i18n'
import ChartTableToggleMenu from 'scoreboard/charts/edit/chart-table-toggle-menu'
import MapDataSizeMenu from './map-data-size-menu'
import ThresholdColorModel from 'scoreboard/components/threshold-colors/threshold-colors-model'
import ThresholdColorsDialog from 'scoreboard/components/threshold-colors/threshold-colors-dialog'
import MapLevelsMenu from './map-levels-menu'
import { geographicTreeRootId } from 'scoreboard/charts/model/chart-designer'
import conductor from 'scoreboard/components/conductor'


const TreeMapRollupLevelsMenu = BootslideMenu.MultipleChoiceMenu.extend({
	property: 'rollupLevels',

	initialize: function() {
		BootslideMenu.MultipleChoiceMenu.prototype.initialize()
		this.label = i18n.get('charts.chart.settingType.visibleHierarchyLevels.visibleLevels')
	}
})

const TreeMapRollupLevelsMenuOption = BootslideMenu.MultipleChoiceOption.extend({
	property: 'rollupLevels',
	initialize: function (options = {}) {
		this.value = options.value
		this.label = options.value
		this.model = options.model

		BootslideMenu.MultipleChoiceOption.prototype.initialize.apply(this, arguments)
	},
	updateModel(value) {
		this.model.set(this.property, value)
	}
})

/**
 * Maps the setting type to the corresponding setting editor
 */
const settingEditors = {
	'colors': ColorSettings,
	'labels': LabelSettings,
	'legends': LegendSettings
}

/**
 * Panel for chart "other" settings
 *
 * @extends ChartDesignerPanel
 */
const OtherSettingsPanel = ChartDesignerPanel.extend({
	className: ChartDesignerPanel.prototype.className + ' other-settings-panel',

	title: 'charts.chart.other',

	/**
	 * @type ChartDesigner
	 */
	model: null,

	/**
	 * @param options
	 * @param {Boolean} [options.hideTableToggle] Show/Hide the chart table
	 * visibility toggle
	 */
	initialize: function(options = {}) {
		ChartDesignerPanel.prototype.initialize.apply(this, arguments)
		this.options = options
		this.model = options.model
		// Holds each settings button so they can be removed
		this.views = []

		this.listenTo(this.model, 'change', () => this._toggleAxesLabelVisibility())
	},

	_toggleAxesLabelVisibility: function() {
		this.$('.axes').toggleClass('is-not-displayed',
			nonAxesPlotType(this.model) || isPolarChart(this.model))
	},

	/**
	 * @override
	 */
	renderActiveContent: function (container) {
		container.html(activeTemplate())
		const settings = this.getAllSettings()
		if (settings.length) {
			_.forEach(settings, this.renderSetting.bind(this))
		}
		this._toggleAxesLabelVisibility()
	},

	toggleActive: function (active) {
		ChartDesignerPanel.prototype.toggleActive.apply(this, arguments)

		if (!active) {
			this.removeTooltips()
		}
	},

	renderSetting(setting) {
		const view = new SettingView({ setting, model: this.model })
		this.$('.settings').append(view.el)
		view.render()
		this.listenTo(view, 'setting-editor', () => {
			this.openSettingsEditor(setting)
		})

		this.views.push(view)
	},

	/**
	 * Combine all settings into a single list that should display in the "other
	 * settings" panel
	 */
	getAllSettings: function() {
		let settings = []
		const settingTypes = _.keys(settingEditors)
		_.forEach(settingTypes, type => {
			// if this is a chart in the scorecards section, there will still
			// be a default legend in the chart title but the legend settings
			// cannot be changed
			const hideLegends = conductor.section() === 'scorecards'
				&& type === 'legends'

			// Clone the setting so any intermediate changes (like adding
			// settingType below) aren't updating the model attributes directly
			const settingsOfType = _.cloneDeep(this.model.get(type))
			if (settingsOfType && !hideLegends) {
				// Set the setting type so we know which editor to display
				_.forEach(settingsOfType, s => s.settingType = type)
				settings = [ ...settings, ...settingsOfType ]
			}
		})

		// Add axes setting
		settings.push({ settingType : 'axes' })

		// Filter out (essentially hiding) any settings for plotTypes that aren't
		// on the chart (could have changed the plotType or removed a serie).
		// We don't actually remove them until the chart is saved, so the user
		// doesn't lose the settings if they're doing something like toggling
		// a serie's plotType
		let usedPlotTypes = _(this.model.features.toArray())
			.map(f => f.get('plotType'))
			.compact()
			.uniq()
			.value()

		// Add total container value
		if (usedPlotTypes.includes('waffle') || usedPlotTypes.includes('filled-container')) {
			settings.push({ settingType: 'totalContainerValue' })
		}

		// #37576 show a "Pie Labels" setting if there is at least one pie serie
		// and there is at least one breakdown
		if ((usedPlotTypes.includes('pie') ||  usedPlotTypes.includes('donut')) &&
			this.model.breakdowns.toArray().length) {
			settings.push({ settingType: 'pieLabels' })
		}

		// #37548 settings for controlling how many hierarchy levels to display
		// for tree maps
		if (usedPlotTypes.includes('tree-map')) {
			settings.push({ settingType: 'visibleHierarchyLevels' })
		}

		if (usedPlotTypes.includes('funnel') || usedPlotTypes.includes('pyramid')) {
			settings.push({ settingType: 'equallySizedStages' })
		}

		const isSpikeMap = usedPlotTypes.includes('spike-map')
		const isBubbleMap = usedPlotTypes.includes('bubble-map')
		const isGeographicMap = usedPlotTypes.includes('filled-map')
			|| isSpikeMap || isBubbleMap

		// geographic map settings
		if (isGeographicMap) {
			settings.push({ settingType: 'mapLevels' })
			settings.push({ settingType: 'startingView' })
		}
		if (isBubbleMap) {
			settings.push({ settingType: 'bubbleSize' })
		}
		if (isSpikeMap) {
			settings.push({ settingType: 'spikeHeight' })
		}

		// Add chart table
		// #40515 This should always be at the bottom
		if (!this.options.hideTableToggle) {
			settings.push({settingType: 'chartTable'})
		}

		settings = settings.filter(setting => {
			if (!setting.plotType) {
				return true
			}

			return _.includes(usedPlotTypes, setting.plotType)
		})

		return settings
	},

	/**
	 * Open a dialog with a form to edit the specified setting type
	 */
	openSettingsEditor: function(setting) {
		switch (setting.settingType) {
			case 'axes':
				this.showAxesConfig()
				return
			case 'totalContainerValue':
				this.showTotalContainerValueSetting(setting)
				return
			case 'pieLabels':
				this.showPieLabelsTooltip(_.extend({
					plotType: this.model.get('plotType')
				}, setting))
				return
			case 'chartTable':
				this.showChartTableToggle()
				return
			case 'equallySizedStages':
				this.showEquallySizedStagesToggle()
				return
			case 'visibleHierarchyLevels':
				this.showVisibleHierarchyLevelsConfig()
				return
			case 'mapLevels':
				this.showMapLevelsConfig()
				return
			case 'startingView':
				this.showStartingViewConfig()
				return
			case 'spikeHeight':
			case 'bubbleSize':
				this.showMapDataSizeConfig(setting.settingType)
				return
			case 'colors':
				if (setting.plotType === 'filled-map') {
					this.showConditionalColoringConfig()
					return
				}
		}

		const constructor = settingEditors[setting.settingType]
		const editor = new constructor({
			setting,
			chartDesigner: this.model
		})
		editor.on('done', newSetting => {
			const settingType = newSetting.settingType
			// Clone the settings array, or model.set() won't consider it a change
			// and won't fire the 'change' event
			const updatedSettings = _.cloneDeep(this.model.get(settingType)) || []
			const index = _.findIndex(updatedSettings, s => s.id == newSetting.id)
			if (index >= 0) {
				// Don't put settingType in the model, since that was just added
				// for some client-side logic
				updatedSettings[index] = _.omit(newSetting, 'settingType')
			}

			this.model.set(settingType, updatedSettings)
			// Trigger a separate change event on the view, because that's what
			// ChartDesignerEditor is listening for (see ChartDesignerEditor.onColorSettingChange()
			// for explanation)
			this.trigger('change:' + settingType)
			this.render()
		})
		appDisplay.dialog(editor)
	},

	showAxesConfig: function() {
		let editMenu = new EditAxesMenu({
			model: this.model
		})
		this.editAxesTooltip = new Tooltip({
			attachTo: this.$('.setting-view.axes'),
			content: editMenu,
			placement: 'right',
			arrow: 'top',
			extraClass: 'has-bootslide-menu chart-designer-tooltip'
		}).render()
	},

	showTotalContainerValueSetting: function() {
		const editor = new TotalContainerValueSetting({
			setting: this.model.get('totalContainerValue'),
			chartDesigner: this.model
		})
		editor.on('done', setting => {
			this.model.set('totalContainerValue', setting)
			this.render()
		})
		appDisplay.dialog(editor)
	},

	showConditionalColoringConfig: function() {
		// conditional colors are currently only possible on filled region maps,
		// for which there can only be a single color so grab the only one here
		const colorSetting = this.model.get('colors')[0]
		const currentSettings = colorSetting.thresholdColoring
		const colorModel = new ThresholdColorModel(currentSettings)
		let view = new ThresholdColorsDialog({
			model: colorModel,
			title: i18n.get('report.designer.conditionalColors')
		})
		view.on('done', model => {
			colorSetting.thresholdColoring = model
			this.model.set('colors', [ colorSetting ])
			this.trigger('change:' + 'colors')
		})
		appDisplay.dialog(view)
	},

	/**
	 * #37576 display a tooltip allowing the showing or hiding of repeating pie
	 * labels
	 */
	showPieLabelsTooltip: function({ plotType }) {
		// plotType is used to differentiate between donut and pie language
		const menuLabelMsg = i18n.get(`charts.chart.${plotType}Labels`)
		const toggleLabelKey = `charts.chart.${plotType}Label`
		const menu = BootslideMenu.extend({
			menu: function() {
				return {
					label: i18n.get('common.edit2', menuLabelMsg),
					target: [
						{
							type: 'toggle',
							label: toggleLabelKey,
							contentView: BootslideMenu.ToggleProperty.extend({
								property: {
									label: toggleLabelKey,
									name: 'pieLabels'
								}
							})
						}
					],
					width: 200
				}
			}
		})

		this.editPieLabelsTooltip = new Tooltip({
			attachTo: this.$('.setting-view.pieLabels'),
			content: new menu({ model: this.model }),
			placement: 'right',
			arrow: 'top',
			extraClass: 'has-bootslide-menu chart-designer-tooltip'
		}).render()
	},

	showChartTableToggle: function() {
		let tableToggleMenu = new ChartTableToggleMenu({
			model: this.model
		})
		this.chartTableTooltip = new Tooltip({
			attachTo: this.$('.setting-view.chartTable'),
			content: tableToggleMenu,
			placement: 'right',
			arrow: 'top',
			extraClass: 'has-bootslide-menu chart-designer-tooltip'
		}).render()
	},

	showMapDataSizeConfig: function(settingType) {
		this.mapDataSizeTooltip = new Tooltip({
			attachTo: this.$(`.setting-view.${settingType}`),
			content: new MapDataSizeMenu({
				model: this.model,
				settingType
			}),
			placement: 'right',
			arrow: 'top',
			extraClass: 'has-bootslide-menu'
		}).render()
	},

	showEquallySizedStagesToggle: function() {
		const menu = BootslideMenu.extend({
			model: null,

			menu: function () {
				return {
					label: i18n.get('charts.chart.settingType.equallySizedStages'),
					target: [{
						type: 'toggle',
						label: 'charts.chart.settingType.equallySizedStages',
						contentView: BootslideMenu.ToggleProperty.extend({
							property: { label: 'charts.chart.settingType.equallySizedStages', name: 'equallySizedStages' }
						})
					}],
					width: 200
				}
			}
		})
		this.chartTableTooltip = new Tooltip({
			attachTo: this.$('.setting-view.equallySizedStages'),
			content: new menu({ model: this.model }),
			placement: 'right',
			arrow: 'top',
			extraClass: 'has-bootslide-menu chart-designer-tooltip'
		}).render()
	},

	showVisibleHierarchyLevelsConfig: function() {
		const menu = BootslideMenu.extend({
			menu: function() {
				return {
					label: i18n.get('charts.chart.settingType.visibleHierarchyLevels'),
					target: [
						{
							type: 'toggle',
							label: 'charts.chart.pieLabel',
							contentView: BootslideMenu.ToggleProperty.extend({
								property: {
									label: 'charts.chart.settingType.visibleHierarchyLevels.showRollupRoot',
									name: 'showRollupRoot'
								}
							})
						},
						{
							label: i18n.get('charts.chart.settingType.visibleHierarchyLevels.visibleLevels'),
							type: 'value-then-submenu',
							contentView: new TreeMapRollupLevelsMenu({ model: this.model }),
							model: this.serie,
							target: [ 1, 2, 3 ].map(value => ({
								contentView: new TreeMapRollupLevelsMenuOption({
									value,
									model: this.model
								})
							}))
						}
					],
					width: 200
				}
			}
		})

		this.editVisibleHierarchiesTooltip = new Tooltip({
			attachTo: this.$('.setting-view.visibleHierarchyLevels'),
			content: new menu({ model: this.model }),
			placement: 'right',
			arrow: 'top',
			extraClass: 'has-bootslide-menu chart-designer-tooltip'
		}).render()
	},

	showMapLevelsConfig: function() {
		this.mapLevelsConfigTooltip = new Tooltip({
			attachTo: this.$('.setting-view.mapLevels'),
			content: new MapLevelsMenu({ model: this.model }),
			placement: 'right',
			arrow: 'top',
			extraClass: 'has-bootslide-menu'
		}).render()
	},

	showStartingViewConfig: function() {
		const menu = new StartingViewMenu({
			// what tree is shown for selection depends on the data type of the
			// geographic field being used
			treeId: geographicTreeRootId({ designer: this.model }),
			selectedId: this.model.get('startingViewTreeId'),
			params: { mapLevels: this.model.getEnabledMapLevels().join(',') }
		})

		appDisplay.dialog(menu)
		menu.on('done', data => this.model.set(data))
	},

	removeTooltips: function() {
		_.invokeMap([this.editAxesTooltip, this.editPieLabelsTooltip,
			this.chartTableTooltip, this.editVisibleHierarchiesTooltip,
			this.colorLegendTooltip, this.mapLevelsConfigTooltip,
			this.mapDataSizeTooltip], 'remove')
	},

	remove: function() {
		ChartDesignerPanel.prototype.remove.apply(this, arguments)
		this.removeTooltips()
		_.invokeMap(this.views, 'remove')
	}
})

export default OtherSettingsPanel
OtherSettingsPanel.__filename__ = 'charts/edit/other-settings-panel.js'
